import React, { useEffect, useRef, useState } from 'react';
import { FileCheck, Calendar, UserCheck, ClipboardList, DollarSign, Lightbulb } from 'lucide-react';

const EmployerServices = () => {
  const [visibleCards, setVisibleCards] = useState([]);
  const cardRefs = useRef([]);

  useEffect(() => {
    const observers = cardRefs.current.map((ref, index) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !visibleCards.includes(index)) {
              setVisibleCards((prev) => [...prev, index]);
            }
          });
        },
        { threshold: 0.1 }
      );

      if (ref) {
        observer.observe(ref);
      }

      return observer;
    });

    return () => {
      observers.forEach((observer, index) => {
        if (cardRefs.current[index]) {
          observer.unobserve(cardRefs.current[index]);
        }
      });
    };
  }, []);

  const services = [
    {
      icon: FileCheck,
      title: 'WCB & WorkSafe Compliance',
      description: 'Reduce your exposure, protect your safety ratings, and save money with WorkNation.',
      gradient: 'from-blue-500 to-blue-600',
    },
    {
      icon: Calendar,
      title: 'Daily & Weekly Worker Placements',
      description: 'Order labour on demand, on your terms. Flexible staffing when you need it.',
      gradient: 'from-indigo-500 to-indigo-600',
    },
    {
      icon: UserCheck,
      title: 'Temp to Perm Placements',
      description: 'Try prospects before you hire. We help you take the risk out of hiring with this innovative program.',
      gradient: 'from-blue-600 to-blue-700',
    },
    {
      icon: ClipboardList,
      title: 'Contract Placements',
      description: 'Need a longer-term employee? We are set up to help with extended contract placements.',
      gradient: 'from-indigo-600 to-indigo-700',
    },
    {
      icon: DollarSign,
      title: 'Payrolling Services',
      description: 'We pay our workforce upfront and can customize payroll solutions for your firm.',
      gradient: 'from-blue-500 to-indigo-600',
    },
    {
      icon: Lightbulb,
      title: 'Creative Staffing Solutions',
      description: 'Share your challenges with us and let our experienced team create a custom solution for your needs.',
      gradient: 'from-indigo-500 to-blue-600',
    },
  ];

  return (
    <section className="py-20 px-6 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-indigo-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" style={{animationDelay: '1.5s'}}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 animate-fade-in-up">
            <span className="gradient-text">Our Services For Employers</span>
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto animate-fade-in-up" style={{animationDelay: '0.2s'}}>
            When you work with WorkNation, you're partnering with a leading staffing agency. Our solid reputation will give you and your company peace of mind.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              ref={(el) => (cardRefs.current[index] = el)}
              className={`group bg-white p-8 rounded-2xl shadow-lg transition-all duration-700 hover:shadow-2xl relative overflow-hidden ${
                visibleCards.includes(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${(index % 3) * 100}ms` }}
            >
              {/* Hover gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/0 to-blue-100/0 group-hover:from-blue-50/50 group-hover:to-blue-100/30 transition-all duration-500"></div>
              
              {/* Icon with gradient background */}
              <div className={`relative inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${service.gradient} rounded-xl mb-6 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 shadow-lg group-hover:shadow-2xl`}>
                <service.icon size={28} className="text-white" />
              </div>
              
              <h3 className="text-xl font-bold text-[#003366] mb-3 group-hover:text-[#004488] transition-colors duration-300 relative z-10">{service.title}</h3>
              <p className="text-gray-700 leading-relaxed relative z-10">{service.description}</p>
              
              {/* Bottom accent line */}
              <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${service.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default EmployerServices;